import { useCallback, useState } from "react";
import dayjs from "dayjs";
import {
  ArrowDownTrayIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@tracify/ui/button";
import { Calendar } from "@tracify/ui/calendar";
import { DataFilter } from "@tracify/ui/data-filter";
import { Popover, PopoverContent, PopoverTrigger } from "@tracify/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectVariants,
} from "@tracify/ui/select";
import { useIsMobile } from "@tracify/ui";
import { cn } from "@lib/utils";
import Spinner from "@tracify/ui/spinner";

import type {
  RecentActivityRange,
  RecentActivityRangeValidation,
} from "./recent-activity-utils";
import {
  formatRecentActivityRangeLabel,
  getRecentActivityDayRange,
  validateRecentActivityRange,
} from "./recent-activity-utils";
import { EVENT_TYPE_OPTIONS } from "./use-heartbeat";

type RecentActivityToolbarProps = {
  selectedEventTypes: string[];
  isReturningCustomer: boolean | null;
  onCustomerStatusChange: (value: boolean | null) => void;
  onEventTypesChange: (eventTypes: string[]) => void;
  range: RecentActivityRange;
  onRangeChange: (range: RecentActivityRange) => void;
  rangeValidation: RecentActivityRangeValidation;
  isLoading: boolean;
  isExporting: boolean;
  hasRows: boolean;
  onDownloadCsv: () => void;
};

export function RecentActivityToolbar({
  selectedEventTypes,
  isReturningCustomer,
  onCustomerStatusChange,
  onEventTypesChange,
  range,
  onRangeChange,
  rangeValidation,
  isLoading,
  isExporting,
  hasRows,
  onDownloadCsv,
}: RecentActivityToolbarProps) {
  const [rangeOpen, setRangeOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<RecentActivityRange>(range);
  const isMobile = useIsMobile();
  const maxSelectableDate = dayjs().endOf("day").toDate();

  const draftValidation = validateRecentActivityRange(
    draftRange,
    maxSelectableDate
  );
  const disableDownload =
    isLoading || isExporting || !hasRows || !rangeValidation.isValid;
  const rangeLabel = formatRecentActivityRangeLabel(range);
  const defaultMonth = isMobile
    ? draftRange.startDate
    : dayjs(draftRange.startDate).isSame(new Date(), "month")
      ? dayjs(draftRange.startDate).subtract(1, "month").toDate()
      : draftRange.startDate;

  const handleRangeOpenChange = useCallback(
    (open: boolean) => {
      setRangeOpen(open);

      if (open) {
        setDraftRange(range);
      }
    },
    [range]
  );

  const handleCalendarSelect = useCallback((selectedDate: Date | undefined) => {
    if (!selectedDate) return;

    setDraftRange(getRecentActivityDayRange(selectedDate));
  }, []);

  const handleApplyRange = useCallback(() => {
    if (!draftValidation.isValid) return;

    onRangeChange(draftRange);
    setRangeOpen(false);
  }, [draftRange, draftValidation.isValid, onRangeChange]);

  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <DataFilter
          variant="alternate"
          size="md"
          title="Event Type"
          value={selectedEventTypes}
          onValueChange={onEventTypesChange}
          options={EVENT_TYPE_OPTIONS}
          disabled={isLoading}
        />

        <Select
          value={
            isReturningCustomer === null ? "all" : String(isReturningCustomer)
          }
          onValueChange={(value) =>
            onCustomerStatusChange(value === "all" ? null : value === "true")
          }
          disabled={isLoading}
        >
          <SelectTrigger
            variant="alternate"
            className="w-fit"
            aria-label="Customer Status"
          >
            <span>
              Customer Status: <SelectValue />
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="false">New</SelectItem>
            <SelectItem value="true">Returning</SelectItem>
            <p className="max-w-64 px-2 py-1.5 text-xs text-muted-foreground">
              New and Returning show only purchases with a known customer
              status.
            </p>
          </SelectContent>
        </Select>

        <Popover open={rangeOpen} onOpenChange={handleRangeOpenChange}>
          <PopoverTrigger
            className={cn(
              selectVariants({ variant: "alternate" }),
              "w-fit max-w-full gap-2",
              !rangeValidation.isValid && "border-destructive"
            )}
            disabled={isLoading}
            aria-invalid={!rangeValidation.isValid}
            aria-label={`Date Range: ${rangeLabel}`}
          >
            <span>{rangeLabel}</span>
            <ChevronUpDownIcon className="size-5 shrink-0" />
          </PopoverTrigger>
          <PopoverContent
            className="w-fit p-0"
            align="start"
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            <div className="flex flex-col items-start gap-3 p-2 lg:p-3">
              <Calendar
                mode="single"
                defaultMonth={defaultMonth}
                selected={draftRange.startDate}
                onSelect={handleCalendarSelect}
                numberOfMonths={isMobile ? 1 : 2}
                weekStartsOn={1}
                showOutsideDays={false}
                disabled={{ after: maxSelectableDate }}
                className="border-none p-0"
                classNames={{
                  today:
                    "[&:not([data-selected=true])]:bg-muted [&:not([data-selected=true])]:text-muted-foreground [&:not([data-selected=true])]:rounded-full",
                }}
              />

              {!draftValidation.isValid && (
                <p className="text-sm text-destructive" role="alert">
                  {draftValidation.message}
                </p>
              )}

              <div className="flex w-full justify-end">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleApplyRange}
                  disabled={!draftValidation.isValid}
                >
                  Apply
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={onDownloadCsv}
        disabled={disableDownload}
        aria-busy={isExporting}
      >
        {isExporting ? (
          <Spinner className="mr-1" size="small" />
        ) : (
          <ArrowDownTrayIcon className="mr-1 h-4 w-4" />
        )}
        Download CSV
      </Button>
    </div>
  );
}
